/**
 * Client-side lead helpers. Thin wrappers over the server /api/lead endpoint
 * (the system of record). All calls are fire-and-forget safe — capture must
 * never block or break the user-facing flow.
 */

export interface LeadPayload {
  email: string;
  destination?: string;
  partyType?: string;
  vibes?: string[];
  budget?: string;
  source: 'day-planner' | 'create' | 'preview' | 'for-business' | 'footer';
  tierIntent?: 'free' | 'day_pass' | 'pro' | 'b2b';
  status?: 'captured' | 'delivered' | 'failed_recovery' | 'converted';
  tags?: string[];
  utm?: Record<string, string>;
}

/** Read UTM params from the current URL (persisted to sessionStorage on first hit). */
export const readUtm = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  const KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl: Record<string, string> = {};
    for (const k of KEYS) {
      const v = params.get(k);
      if (v) fromUrl[k] = v.slice(0, 200);
    }
    if (Object.keys(fromUrl).length) {
      sessionStorage.setItem('aura_utm', JSON.stringify(fromUrl));
      return fromUrl;
    }
    const stored = sessionStorage.getItem('aura_utm');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/** Capture a lead. Never throws. Returns true on success. */
export const captureLead = async (payload: LeadPayload): Promise<boolean> => {
  try {
    const res = await fetch('/api/lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, utm: payload.utm || readUtm() }),
    });
    return res.ok;
  } catch {
    return false;
  }
};

/** Fire funnel analytics events across GA4 + Meta consistently. */
export const trackFunnel = (event: string, params: Record<string, any> = {}): void => {
  try { (window as any).gtag?.('event', event, params); } catch { /* gtag absent */ }
  try {
    const fbqMap: Record<string, string> = {
      email_capture: 'Lead',
      checkout_start: 'InitiateCheckout',
      purchase: 'Purchase',
    };
    if (fbqMap[event]) (window as any).fbq?.('track', fbqMap[event], params);
  } catch { /* pixel absent */ }
};
